/**
 * lazy-load.js
 *
 * Native loading="lazy" starts fetching roughly 1250-2500px before an image
 * reaches the viewport, and it treats anything with a layout box inside the
 * viewport as on screen. Neither helps here:
 *
 *   - the hero slideshow stacks every slide at the same position, so slides 2+
 *     count as on screen and downloaded on first load (302KB of banners that
 *     nobody had asked to see yet);
 *   - section images 2600px down still arrived during the initial load.
 *
 * Images that should wait render with data-src / data-srcset / data-sizes and a
 * data-lazy-img marker instead of real sources. This swaps them in:
 *
 *   data-lazy-img="visible"      an IntersectionObserver reveals it as it nears
 *                                the viewport - a tight margin while the page is
 *                                still loading, wider once it has finished, so
 *                                the next screen is ready before it is reached.
 *   data-lazy-img="interaction"  revealed on the first scroll / pointer / key /
 *                                touch anywhere on the page. For images that sit
 *                                inside the viewport but are not actually being
 *                                looked at, like the slides behind slide one.
 *
 * window.themeLazyImages.reveal(root) reveals everything inside root at once -
 * call it after injecting markup (the tab panels do).
 */
(function () {
  'use strict';

  var ANY = '[data-lazy-img]';
  var ON_VISIBLE = '[data-lazy-img="visible"]';
  var ON_INTERACTION = '[data-lazy-img="interaction"]';

  function swap(el) {
    var sizes = el.getAttribute('data-sizes');
    var srcset = el.getAttribute('data-srcset');
    var src = el.getAttribute('data-src');
    if (sizes) {
      el.setAttribute('sizes', sizes);
      el.removeAttribute('data-sizes');
    }
    if (srcset) {
      el.setAttribute('srcset', srcset);
      el.removeAttribute('data-srcset');
    }
    if (src) {
      el.setAttribute('src', src);
      el.removeAttribute('data-src');
    }
  }

  function reveal(el) {
    if (!el || !el.hasAttribute('data-lazy-img')) return;

    // <source> elements of a <picture> have to be swapped before the <img>,
    // otherwise the browser settles on the img's src first.
    var parent = el.parentNode;
    if (parent && parent.tagName === 'PICTURE') {
      parent.querySelectorAll('source[data-srcset]').forEach(swap);
    }
    swap(el);
    el.removeAttribute('data-lazy-img');

    // Clear the shimmer once the real image has decoded.
    var holder = el.closest('.lazy-img-holder') || parent;
    function done() {
      if (holder && holder.classList) holder.classList.add('lazy-img-holder--loaded');
      el.classList.add('lazy-img--loaded');
    }
    // Deferred images carry a transparent 1x1 as their placeholder src, which
    // counts as complete. Treating that as loaded would drop the shimmer while
    // the real image was still on its way, so only a non-placeholder source
    // ends the wait.
    function settled() {
      return el.complete && el.naturalWidth > 0 && (el.currentSrc || '').slice(0, 5) !== 'data:';
    }
    if (settled()) done();
    else {
      el.addEventListener('load', function () {
        if (settled()) done();
      });
      el.addEventListener('error', done, { once: true });
    }
  }

  function revealWithin(root, selector) {
    (root || document).querySelectorAll(selector || ANY).forEach(reveal);
  }

  window.themeLazyImages = {
    reveal: function (root) {
      revealWithin(root, ANY);
    },
  };

  // Scroll is listened to without capture on purpose: a scroll inside one of
  // the page's own scrollable strips does not bubble, but a capturing listener
  // on window would still see it and fire these far too early.
  var interacted = false;
  var INTERACTION = ['scroll', 'pointerdown', 'pointermove', 'touchstart', 'keydown', 'wheel', 'click'];

  // Does this event represent a person doing something?
  //
  // Not every scroll event is one. The page moves itself about during startup -
  // sliders calling scrollIntoView, the sticky bar measuring - and each of those
  // reaches window as a perfectly ordinary scroll. Taking them at face value
  // released everything below the fold seconds after load with nobody there,
  // which is what kept the recommendations strip loading.
  //
  // So a scroll only counts once the page has actually moved, and synthesised
  // events never count.
  var startScrollY = window.pageYOffset || 0;
  function isRealInteraction(ev) {
    if (!ev || ev.isTrusted === false) return false;
    if (ev.type !== 'scroll') return true;
    return Math.abs((window.pageYOffset || 0) - startScrollY) > 2;
  }

  // --- "interaction" -------------------------------------------------------
  // Always bound, even on a page with no "interaction" images: the flag also
  // releases AJAX content that arrived before the visitor did anything.
  function watchInteraction() {
    function onIntent(ev) {
      if (!isRealInteraction(ev)) return;
      interacted = true;
      INTERACTION.forEach(function (name) {
        window.removeEventListener(name, onIntent);
      });
      revealWithin(document, ON_INTERACTION);
      flushPendingRoots();
    }
    INTERACTION.forEach(function (name) {
      // Not `once`: a scroll that turns out to be the page moving itself must
      // not consume the listener.
      window.addEventListener(name, onIntent, { passive: true });
    });
  }

  // --- "visible" -----------------------------------------------------------
  var currentIO = null;

  // Hand markup that arrived after load (AJAX sections) to the live observer,
  // so it obeys the same rules as everything else.
  //
  // Held back until the document is complete and a frame has passed. An AJAX
  // section is injected while the page is still growing - gallery images
  // resolving, other sections filling in - so for a moment it sits near the top
  // of a short page and the observer calls it visible. That is how the
  // recommendations strip, two screens down, kept being fetched by a visitor
  // who had not scrolled.
  // Roots that arrived before the first interaction, observed once it happens.
  var pendingRoots = [];
  function flushPendingRoots() {
    var roots = pendingRoots;
    pendingRoots = [];
    roots.forEach(function (r) {
      observeWithin(r);
    });
  }
  function observeWithin(root) {
    function go() {
      requestAnimationFrame(function () {
        if (!currentIO) return;
        (root || document).querySelectorAll(ON_VISIBLE).forEach(function (img) {
          currentIO.observe(img);
        });
      });
    }

    // Injected content is only handed to the observer once the visitor has
    // interacted. Observing it before that kept revealing the recommendations
    // strip - two screens down - on roughly two loads in three: the section
    // arrives while the page is still growing, so for a moment it genuinely is
    // near the viewport and every geometry check agrees. Waiting for an
    // interaction removes the race rather than narrowing it, and nothing below
    // the fold is wanted before then anyway.
    if (interacted) {
      if (document.readyState === 'complete') go();
      else window.addEventListener('load', go, { once: true });
      return;
    }
    pendingRoots.push(root || document);
  }


  function watchVisible() {
    if (!('IntersectionObserver' in window)) {
      revealWithin(document, ON_VISIBLE);
      return;
    }

    function observe(margin) {
      var allowance = parseInt(margin, 10) || 0;
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;

            // Position is trustworthy once the document has been parsed: every
            // deferred image carries width and height, so the page is already
            // at its real height even though nothing has decoded yet.
            //
            // Deliberately not `load`. Waiting for that made each below-fold
            // image sit on its shimmer until the hero video and the third-party
            // scripts had finished - load lands around 17s here against 6s for
            // DOMContentLoaded, so images appeared some thirteen seconds after
            // the page was ready to show them.
            if (document.readyState === 'loading' && !interacted) return;

            var box = entry.target.getBoundingClientRect();
            if (box.top - allowance > (window.innerHeight || 0)) return;

            io.unobserve(entry.target);
            reveal(entry.target);
          });
        },
        { rootMargin: margin + ' 0px' }
      );
      document.querySelectorAll(ON_VISIBLE).forEach(function (img) {
        io.observe(img);
      });
      currentIO = io;
      return io;
    }

    // Before any interaction: strictly what is on screen, no margin.
    //
    // A margin here defeats the point. The section below the hero starts about
    // 150px past the fold, so even a 150px margin revealed its whole image row
    // during the initial load - the exact thing native loading="lazy" was doing.
    var near = observe('0px');

    // An element the observer already reported stays reported; it will not fire
    // again while it keeps intersecting. Anything the guard above turned away
    // during loading would therefore never get a second look, so re-observe
    // once the layout has settled and the measurements mean something.
    function recheck() {
      if (widened) return;
      if (near) near.disconnect();
      near = observe('0px');
    }
    if (document.readyState === 'complete') requestAnimationFrame(recheck);
    else window.addEventListener('load', function () { requestAnimationFrame(recheck); }, { once: true });

    // Widen only once the visitor has actually done something. Widening on the
    // load event instead would download the next screens for every visitor,
    // including the ones who never scroll.
    var widened = false;
    function widen(ev) {
      if (widened || !isRealInteraction(ev)) return;
      widened = true;
      INTERACTION.forEach(function (name) {
        window.removeEventListener(name, widen);
      });
      if (near) near.disconnect();
      // Roughly half a screen ahead. Wide enough that images are ready by the
      // time they are scrolled to, narrow enough that the first scroll does not
      // release a hundred requests at once on a page this long.
      observe('400px');
    }
    INTERACTION.forEach(function (name) {
      window.addEventListener(name, widen, { passive: true });
    });
  }

  // --- CSS background images ----------------------------------------------
  // A background-image cannot carry loading="lazy", so the rule itself is kept
  // behind a class that only goes on once the element nears the viewport.
  // Markup opts in with data-lazy-bg; the section's CSS scopes the
  // background-image to .lazy-bg--on.
  function watchBackgrounds() {
    var nodes = document.querySelectorAll('[data-lazy-bg]');
    if (!nodes.length) return;
    if (!('IntersectionObserver' in window)) {
      nodes.forEach(function (el) {
        el.classList.add('lazy-bg--on');
      });
      return;
    }
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          io.unobserve(entry.target);
          entry.target.classList.add('lazy-bg--on');
          entry.target.removeAttribute('data-lazy-bg');
        });
      },
      { rootMargin: '200px 0px' }
    );
    nodes.forEach(function (el) {
      io.observe(el);
    });
  }

  function init() {
    watchInteraction();
    watchVisible();
    watchBackgrounds();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Markup injected by JS - the quick view modal, AJAX panels - never runs the
  // inline reveal script that sits beside it, because a <script> inserted with
  // innerHTML does not execute. Without this the quick view opened showing alt
  // text where the product images should be.
  //
  // "visible" images are handed to the observer so they still follow the normal
  // rules. "interaction" images are released only if the visitor has already
  // interacted - which they have, since opening a quick view is a click - as
  // the page-level trigger fired once and will not fire again.
  function adoptInjected() {
    observeWithin(document);
    if (interacted) revealWithin(document, ON_INTERACTION);
  }

  if ('MutationObserver' in window) {
    var adoptTimer = null;
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType !== 1) continue;
          if ((n.matches && n.matches(ANY)) || (n.querySelector && n.querySelector(ANY))) {
            clearTimeout(adoptTimer);
            adoptTimer = setTimeout(adoptInjected, 80);
            return;
          }
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  // Sections that arrive after load: the theme editor re-rendering one, and
  // Shopify's AJAX product recommendations, which fire the same event.
  //
  // These are observed, not revealed. Revealing meant the recommendations strip
  // - five full-size product images, ~340KB, two screens down - was fetched the
  // moment it was injected, with nobody having scrolled anywhere near it.
  //
  // The theme editor is the exception: there, a deferred image can sit empty
  // while the merchant is looking straight at it.
  document.addEventListener('shopify:section:load', function (e) {
    if (window.Shopify && window.Shopify.designMode) {
      revealWithin(e.target, ANY);
      return;
    }
    observeWithin(e.target);
    // Interaction has already happened if its listeners are gone, so anything
    // waiting on it would otherwise never be released.
    if (interacted) revealWithin(e.target, ON_INTERACTION);
  });
})();
